import "./chart.scss";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend,
  LineChart,
  Line,
  BarChart,
  Bar,
} from "recharts";
import { parsePaymentDate } from "../../utils/date";

const chartPalette = ["#261134", "#6b4fa5", "#ffcc00", "#7b8fa6", "#1d9bf0"];

const monthName = (date) =>
  `${date.toLocaleString("en-US", { month: "short" })} ${date.getFullYear()}`;

const monthKey = (date) =>
  `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`;

const getRecentMonthWindow = (months = 6) => {
  const now = new Date();
  const windowLabels = [];
  for (let i = months - 1; i >= 0; i--) {
    const cursor = new Date(now.getFullYear(), now.getMonth() - i, 1);
    windowLabels.push({
      key: monthKey(cursor),
      name: monthName(cursor),
    });
  }
  return windowLabels;
};

const buildSponsorshipTrend = (sponsors = [], months = 6) => {
  const keys = getRecentMonthWindow(months);
  const result = Object.fromEntries(
    keys.map((item) => [item.key, { name: item.name, Total: 0 }]),
  );

  (sponsors || []).forEach((sponsor) => {
    const d = parsePaymentDate(sponsor.mentored_on_date);
    const key = monthKey(d);
    if (result[key]) {
      result[key].Total += 1;
    }
  });

  return keys.map((item) => result[item.key]);
};

const buildCategoryStats = (sponsors = []) => {
  const grouped = (sponsors || []).reduce((acc, sponsor) => {
    const key = sponsor.category_name || "Uncategorized";
    if (!acc[key]) {
      acc[key] = {
        name: key,
        value: 0,
        color: sponsor.category_color || "#8b9eb2",
      };
    }
    acc[key].value += 1;
    return acc;
  }, {});
  return Object.values(grouped);
};

const Chart = ({ title, paymentData }) => {
  const data = paymentData || [];

  return (
    <div className="chart">
      <div className="title">{title}</div>
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart
          data={data}
          margin={{ top: 10, right: 12, left: 0, bottom: 0 }}
        >
          <defs>
            <linearGradient id="total" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor="#261134" stopOpacity={0.8} />
              <stop offset="95%" stopColor="#261134" stopOpacity={0} />
            </linearGradient>
          </defs>
          <XAxis dataKey="name" stroke="gray" />
          <CartesianGrid strokeDasharray="3 3" className="chartGrid" />
          <Tooltip />
          <Area
            type="monotone"
            dataKey="Total"
            stroke="#261134"
            fillOpacity={1}
            fill="url(#total)"
          />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
};

export default Chart;

export const CategoryDistributionChart = ({ data = [], title }) => {
  const aggregated = buildCategoryStats(data);
  const total = aggregated.reduce((sum, item) => sum + item.value, 0);

  if (!aggregated.length) {
    return (
      <div className="chart emptyChart">
        <div className="title">{title}</div>
        <p>No sponsorship data yet.</p>
      </div>
    );
  }

  const chartData = aggregated.map((item) => ({
    ...item,
    percent: total ? `${Math.round((item.value / total) * 100)}%` : "0%",
  }));

  return (
    <div className="chart">
      <div className="title">{title}</div>
      <ResponsiveContainer width="100%" height="100%">
        <PieChart>
          <Pie
            data={chartData}
            dataKey="value"
            nameKey="name"
            cx="50%"
            cy="45%"
            innerRadius="42%"
            outerRadius="62%"
            paddingAngle={2}
            label={({ name, percent }) => `${name} (${percent})`}
          >
            {chartData.map((entry, index) => (
              <Cell key={`cell-${index}`} fill={entry.color} />
            ))}
          </Pie>
          <Tooltip />
          <Legend
            layout="horizontal"
            verticalAlign="bottom"
            align="center"
            wrapperStyle={{ fontSize: 11, paddingTop: 4 }}
          />
        </PieChart>
      </ResponsiveContainer>
    </div>
  );
};

export const SponsorTrendChart = ({ data = [], title }) => {
  const series = buildSponsorshipTrend(data, 6);

  if (!series.length) {
    return (
      <div className="chart emptyChart">
        <div className="title">{title}</div>
        <p>No trend data yet.</p>
      </div>
    );
  }

  return (
    <div className="chart">
      <div className="title">{title}</div>
      <ResponsiveContainer width="100%" height="100%">
        <BarChart
          data={series}
          margin={{ top: 8, right: 8, left: 0, bottom: 4 }}
        >
          <CartesianGrid strokeDasharray="3 3" className="chartGrid" />
          <XAxis dataKey="name" tick={{ fontSize: 11 }} interval={0} />
          <YAxis allowDecimals={false} width={28} tick={{ fontSize: 11 }} />
          <Tooltip />
          <Bar dataKey="Total" fill={chartPalette[0]} radius={[5, 5, 0, 0]} />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
};

export const SponsorshipTrendLineChart = ({ data = [], title }) => {
  const series = buildSponsorshipTrend(data, 6);

  if (!series.length) {
    return (
      <div className="chart emptyChart">
        <div className="title">{title}</div>
        <p>No trend data yet.</p>
      </div>
    );
  }

  return (
    <div className="chart">
      <div className="title">{title}</div>
      <ResponsiveContainer width="100%" height="100%">
        <LineChart
          data={series}
          margin={{ top: 8, right: 8, left: 0, bottom: 4 }}
        >
          <CartesianGrid strokeDasharray="3 3" className="chartGrid" />
          <XAxis dataKey="name" tick={{ fontSize: 11 }} interval={0} />
          <YAxis allowDecimals={false} width={28} tick={{ fontSize: 11 }} />
          <Tooltip />
          <Line
            type="monotone"
            dataKey="Total"
            stroke={chartPalette[1]}
            strokeWidth={3}
            dot={{ r: 2 }}
          />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
};
